import React, { useEffect, useState } from "react";
import { Button, Form, Input, Select, Typography } from "antd";
import { User, Mail, Phone, Globe, ArrowRight } from "lucide-react";
import styles from "./index.module.css";
import { getFields, getForm, getSteps, updateStep } from "../../../store/policy";
import { useDispatch, useSelector } from "react-redux";
import { FormItem } from "../../common/Utils";
import { saveStep } from "../../../store/policy";
import { countryCodes } from "./countries";
import { validationRules } from "../../common/Utils";

const { Option } = Select;
const { Title, Text } = Typography;

function EnhancedStep1({ setCurrent, current }) {
  const dispatch = useDispatch();
  const [form1] = Form.useForm();
  const [sortedFields, setSortedFields] = useState([]);
  const existingValues = sessionStorage.getItem("values");
  const { steps, fields } = useSelector((state) => state.policySlice);
  const [coverageCountry, setCoverageCountry] = useState("");
  const [residenceCountry, setResidenceCountry] = useState("");
  const [dialCode, setDialCode] = useState("254");

  const onFinish = (values) => {
    const formValues = form1.getFieldsValue();
    if (coverageCountry) {
      localStorage.setItem("coverageCountry", coverageCountry);
    }
    if (dialCode && formValues.phone_number) {
      const value = formValues.phone_number;
      formValues.phone_number = `${dialCode}-${value}`;
    }
    if (
      !form1.isFieldsTouched() &&
      existingValues &&
      JSON.parse(existingValues).find(
        (step) =>
          step.step_id === steps.find((s) => s?.step_number === current)?.id
      )
    ) {
      setCurrent(2);
    } else if (
      existingValues &&
      JSON.parse(existingValues).find(
        (step) =>
          step.step_id === steps.find((s) => s?.step_number === current)?.id
      )
    ) {
      dispatch(
        updateStep({
          userId: JSON.parse(sessionStorage.getItem("user"))?.userId,
          stepId: steps.find((s) => s?.step_number === current)?.id,
          parameters: fields?.map((field, i) => {
            return {
              field_id: field?.id,
              key: field?.field_name,
              value: formValues[field?.field_name],
            };
          }),
        })
      ).then(() => {
        localStorage.removeItem("lastStep");
        setCurrent(2);
      });
    } else {
      dispatch(
        saveStep({
          source: steps?.find((s) => s?.step_number === current)?.step_source,
          user_id: JSON.parse(sessionStorage.getItem("user"))?.userId,
          step_id: steps?.find((s) => s?.step_number === current)?.id,
          parameters: fields?.map((field, i) => {
            return {
              field_id: field?.id,
              key: field?.field_name,
              value: formValues[field?.field_name],
            };
          }),
        })
      ).then(() => setCurrent(2));
    }
  };

  useEffect(() => {
    if (
      steps &&
      steps.find((s) => s?.step_number === current) &&
      steps.find((s) => s?.step_number === current).form_id &&
      steps.find((s) => s?.step_number === current).id
    ) {
      dispatch(
        getFields({
          formId: steps.find((s) => s?.step_number === current).form_id,
          stepId: steps.find((s) => s?.step_number === current).id,
        })
      ).then((res) => {
        if (res?.payload && Array.isArray(res?.payload)) {
          const localFields = res?.payload &&
            Array.isArray(res?.payload) && [...res?.payload];
          setSortedFields(
            localFields?.sort((a, b) => a?.field_sequence - b?.field_sequence)
          );
        }
      });
    }
  }, [steps]);

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user?.email) {
      form1.setFieldsValue({ email: user?.email });
    }
  }, []);

  useEffect(() => {
    if (
      existingValues &&
      steps &&
      JSON.parse(existingValues)?.find(
        (step) =>
          step.step_id === steps.find((s) => s?.step_number === current)?.id
      )
    ) {
      const values = JSON.parse(existingValues).find(
        (step) =>
          step.step_id === steps.find((s) => s?.step_number === current)?.id
      ).parameters;
      const obj = new Object();
      for (let key of values) {
        if (key.key === "phone_number") {
          let value = key.value;
          value = value.split('-');
          obj[key.key] = value[1];
          setDialCode(value[0]);
        } else {
          obj[key.key] = key.value;
        }
      }
      form1.setFieldsValue(obj);
    }
  }, [steps]);

  const getFieldIcon = (fieldName) => {
    switch (fieldName) {
      case 'first_name':
      case 'last_name':
        return <User size={16} color="#028D86" />;
      case 'email':
        return <Mail size={16} color="#028D86" />;
      case 'phone_number':
        return <Phone size={16} color="#028D86" />;
      case 'policy_coverage_country':
      case 'residence_country':
        return <Globe size={16} color="#028D86" />;
      default:
        return <User size={16} color="#028D86" />;
    }
  };

  const PhoneInput = ({ value, onChange, ...rest }) => {
    return (
      <div className={styles.phoneWrapper}>
        <Select
          showSearch
          value={dialCode}
          onChange={(val) => setDialCode(val)}
          className={styles.phoneCode}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.searchValue.toLowerCase().includes(input.toLowerCase())
          }
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          options={countryCodes.map(country => ({
            value: country.dialCode,
            label: `${country.flag || ''} ${country.dialCode}`,
            searchValue: `${country.name} ${country.dialCode}`
          }))}
          dropdownRender={(menu) => (
            <div>
              <div style={{ padding: '8px 12px', fontWeight: '600', borderBottom: '1px solid #f0f0f0', color: '#334155', fontSize: '0.85rem' }}>
                Select Country Code
              </div>
              {menu}
            </div>
          )}
        />
        <Input
          allowClear
          className={styles.phoneNumber}
          placeholder="Enter phone number"
          value={value}
          onChange={onChange}
        />
      </div>
    );
  };

  return (
    <div className={styles.stepWrapper}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardIcon}>
            <User size={20} />
          </div>
          <div>
            <Title level={4} className={styles.cardTitle}>Your Information</Title>
            <Text className={styles.cardDesc}>
              Provide your personal details to customize your quote
            </Text>
          </div>
        </div>

        <Form form={form1} layout="vertical" onFinish={onFinish} className={styles.form}>
          <div className={styles.formGrid}>
            {sortedFields &&
              sortedFields.length > 0 &&
              sortedFields.map((field, index) => {
                return (
                  <Form.Item
                    key={index}
                    label={
                      <span className={styles.fieldLabel}>
                        {getFieldIcon(field?.field_name)}
                        {field?.label}
                      </span>
                    }
                    id={field?.id}
                    name={field?.field_name}
                    rules={validationRules(field?.validation, field?.label)}
                    className={styles.formItem}
                  >
                    {field?.type === "text" && field?.field_name === 'phone_number' ? (
                      <PhoneInput />
                    ) : field?.type === "text" ? (
                      <Input
                        allowClear
                        className={styles.fieldInput}
                        value={field?.value}
                        placeholder={`Enter ${field?.label.toLowerCase()}`}
                        disabled={field?.field_name === "email"}
                      />
                    ) : field?.type === "dropdown" ? (
                      <Select
                        allowClear
                        showSearch
                        placeholder={`Select ${field?.label.toLowerCase()}`}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={
                          field?.field_name === "policy_coverage_country"
                            ? field?.dropdowns?.[0]?.values?.map((opt) => {
                                return { label: opt?.name, value: opt?.value };
                              })
                            : countryCodes.map(country => {
                                return {
                                  label: country.name,
                                  value: country.name,
                                  searchValue: country.name
                                };
                              })
                        }
                        onChange={(e) => {
                          if (field?.field_name === "policy_coverage_country") {
                            setCoverageCountry(e);
                          }
                          if (field?.field_name === "residence_country") {
                            setResidenceCountry(e);
                          }
                        }}
                        className={styles.fieldSelect}
                        value={field?.value}
                      />
                    ) : (
                      ""
                    )}
                  </Form.Item>
                );
              })}
          </div>

          <div className={styles.actions}>
            <Button
              htmlType="submit"
              type="primary"
              className={styles.continueBtn}
              size="large"
            >
              <span>Continue</span>
              <ArrowRight size={16} />
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default EnhancedStep1;
